import React, {useEffect, useState} from 'react';

import {
  useAddTeamsToFolder,
  useRemoveTeamsFromFolder,
  useUpdateFolder,
} from 'app/folders/api';
import {createFormErrorsContext} from 'app/general/forms';
import StateButton from 'app/general/widgets/StateButton';
import {gt} from 'app/utils/i18n';

import {
  FolderDescription,
  FolderName,
  FolderTeamSelector,
  FolderThumbnail,
} from './FolderForm';

import type {ActionMeta} from 'app/components/general/SweetReactSelect';
import type {FormErrorsProps} from 'app/general/forms';
import type {
  AttachedImage,
  BasicTeamSerializedType,
  FolderDescriptionSerializedType,
  FolderSerializedType,
} from 'sweet-types';

export interface DoChangeProps {
  name: string;
  description: Partial<FolderDescriptionSerializedType>;
  teams: Array<BasicTeamSerializedType>;
}

export interface Props extends FormErrorsProps {
  folder: FolderSerializedType;
  onCancel: () => void;
  allowedTeams: Array<BasicTeamSerializedType>;
  isSelectOnly: boolean;
  autoFocus?: boolean;
}

export const ChangeFolder = createFormErrorsContext(
  ({
    folder,
    onCancel,
    allowedTeams,
    isSelectOnly,
    autoFocus,
    captureFormErrorsFromRejection,
  }: Props) => {
    const [name, setName] = useState<string>(folder.name);
    const [content, setContent] = useState<string>(folder.description.content);
    const [thumbnails, setThumbnails] = useState<Array<AttachedImage>>(
      folder.description.images
    );
    const [teams, setTeams] = useState<Array<BasicTeamSerializedType>>(
      folder.teams
    );

    const [updateFolder, {isError, isLoading, error}] = useUpdateFolder();
    const [addTeamsToFolders] = useAddTeamsToFolder();
    const [removeTeamFromFolder, {isError: isRemoveTeamError}] =
      useRemoveTeamsFromFolder();

    useEffect(() => {
      if (isError && error) {
        captureFormErrorsFromRejection(error);
      }
    }, [isError, error]);

    useEffect(() => {
      if (isRemoveTeamError) {
        setTeams(folder.teams);
      }
    }, [isRemoveTeamError]);

    function doChangeFolder() {
      updateFolder({
        folder,
        toUpdate: {
          name,
          description: {content},
        },
      })
        .unwrap()
        .then(onCancel);
    }

    function setTeamOnChange(
      teams: Array<BasicTeamSerializedType>,
      action: ActionMeta<BasicTeamSerializedType>
    ) {
      if (action.action === 'select-option') {
        setTeams((teams) => [...teams, action.option]);
        addTeamsToFolders({folder, urls: [action.option.url]});
      } else if (action.action === 'remove-value') {
        setTeams((teams) =>
          teams.filter((team) => team.url !== action.removedValue.url)
        );
        removeTeamFromFolder({folder, teamURL: action.removedValue.url});
      }
      return Promise.resolve(true);
    }

    return (
      <>
        <div className="col-12 mb-2">
          <div className="d-flex align-items-center">
            <FolderThumbnail
              thumbnails={thumbnails}
              onChange={(thumbnails) => {
                updateFolder({
                  folder,
                  toUpdate: {description: {images: thumbnails}},
                });
                setThumbnails(thumbnails);
              }}
            />
            <FolderName
              autoFocus={autoFocus ?? false}
              name={name}
              onChange={setName}
            />
          </div>
          <FolderDescription
            objectId={folder.id}
            onCancel={onCancel}
            content={content}
            onChange={setContent}
          />
          <FolderTeamSelector
            teams={teams}
            onTeamChange={setTeamOnChange}
            allowedTeams={allowedTeams}
            isSelectOnly={isSelectOnly}
          />
        </div>
        <div className="mt-4 col-12 text-right">
          <button
            className="btn btn-link text-muted"
            type="button"
            onClick={onCancel}
          >
            <i className="fa fa-fw fa-times" /> {gt('Cancel')}
          </button>
          <StateButton onClick={doChangeFolder} isIdle={!isLoading}>
            {gt('Update Folder')}
          </StateButton>
        </div>
      </>
    );
  }
);
